import React, { useState, useEffect, useRef } from "react";
import { Modal, Spinner } from "react-bootstrap";
import styleClasses from "../../Header/Header.module.scss";
import CartItemsList from "../CartItems/CartItemsList";
import SuggestedItemsList from "../SuggestedItems/SuggestedItemsList";
import Billing from "../Billing/Billing";
import { setLocation, useStoreLocation } from "../../../context/StoreLocation";
import useGetRewards from "../../../react-query-hooks/useGetRewards";
import { useAuthState } from "../../../context/UserAuthentication";
import { Link, useLocation } from "react-router-dom";
import {
  DELIVERY_DESTINATION_ID,
  DELIVERY_ORDER_TYPE,
  GOOD_LIFE_LANE_DESTINATION_ID,
  GUEST_USER,
  IN_STORE_DESTINATION_ID,
  PICK_UP_METHOD_LEGACY_GOOD_LIFE_LANE,
  ROUTE_MENU,
} from "../../../constants";
import { useAppSelector } from "../../../redux/store/store";
import { useDispatch } from "react-redux";
import {
  setAutoRedeem,
  deleteItemFromOrder,
  updateItemQuantity,
  resetCartOffer,
  updateItemKey,
  updateCartRedeemedOfferItem,
} from "../../../redux/slices/cartSlice";
import { useQueryClient } from "react-query";
import useDeleteItemFromCart from "../../../react-query-hooks/Cart/useDeleteItem";
import useUpdateItemQuantity from "../../../react-query-hooks/Cart/useUpdateItemQuantity";
import {
  IDeleteOrderItemPayload,
  IUpdateOrderQuantityPayload,
  IUseCoupon,
} from "../../../models/order.model";
import { getUser, getVisitorId, isGuestUser, shouldRelease } from "../../../helpers/helperMethods";
import useToggleAutoRedeem from "../../../react-query-hooks/Cart/useToggleAutoRedeem";
import { Toast_Func } from "../../../helpers/toast.helper";
import UpsellItemsLists from "../UpsellItems/UpsellItemsList";
import useSuggestedItems from "../../../react-query-hooks/useSuggestedItems";
import useSyncCart from "../../../hooks/useSyncCart";
import promoStyleClasses from './Cart.module.scss';
import useCoupon from "../../../react-query-hooks/Cart/useCoupon";
import OfferItemsList from "../Offers/OfferItemsList";
import useGetOffers from "../../../react-query-hooks/useGetOffers";
import useRedeemOffer from "../../../hooks/useRedeemOffer";
import {customerBusinessService, locationMenuService, orderMicroService} from "../../../services";
import {updateTempLoyalityPoints} from "../../../redux/slices/loyaltySlice";
import { getPointsRedeemedInCart } from "../../../helpers/cartHelperMethods";
import {IOfferItem, cartItem} from "../../../models/cart.model";
import OfferEligibilityModal from "../Offers/OfferEligibilityModal";
import { getDestination } from "../../../helpers/checkout/PlaceOrderHelper";
import { brazeLogEventClaimPromoCode } from "../../../helpers/brazeHelper";
import AutoRedeemToggle from "../Rewards/AutoRedeemToggle";
import { bugSnagNotifyAPIError } from "../../../helpers/bugSnagHelper";
import useGetUpsellItemsForAddToOrder from "../../../react-query-hooks/useGetUpsellItemsForAddToOrder";
import useCheckMobileScreen from "../../../hooks/useCheckMobileScreen";
import { useIsDesignV2 } from "../../../hooks/useIsDesignV2";
import { PULSE_VIEW_ID, trackPulseView } from "../../../analytics/pulseViewIds";
import {
  PULSE_CLICK_ACTION,
  trackCartPageClick,
} from "../../../analytics/pulseClickIds";

interface ICartModal {
  showModal: boolean;
  closeModal: () => void;
  addToOrder: any;
}

export default function CartModal({
  showModal,
  closeModal,
  addToOrder
}: ICartModal) {
  const { authInfo } = useAuthState();
  const { locationInfo, dispatch: locationDispatch } = useStoreLocation();
  const location = useLocation();
  const cartViewTrackedForOpenRef = useRef(false);

  useEffect(() => {
    if (!showModal) {
      cartViewTrackedForOpenRef.current = false;
      return;
    }
    if (cartViewTrackedForOpenRef.current) return;
    cartViewTrackedForOpenRef.current = true;
    trackPulseView(PULSE_VIEW_ID.CART, { page_path: location.pathname });
  }, [showModal, location.pathname]);

  const cart = useAppSelector((state) => state.cart);
  const cartItems = cart.items;
  const isCartEmpty = !cartItems.length;
  const autoRedeem = cart.auto_redeem_rewards;

  let { mutate: deleteItem, isLoading: isDeleting } = useDeleteItemFromCart();
  let { mutate: applyCoupon, isLoading: isApplyingCoupon } = useCoupon();
  let { mutate: updateQuantity, isLoading: isUpdatingQuantity } = useUpdateItemQuantity();
  const [reFetchingCart, setReFetchingCart] = useState<boolean>(false);
  const [rewardsDiscountedSubTotal, setRewardsDiscountedSubTotal] = useState<number>(0);
  const [couponCode, setCouponCode] = useState<string>("");
  const visitor_id = getVisitorId()
  const { syncCart, loading: syncingCart } = useSyncCart();
  const location_id = locationInfo.selectedStore?.id
  const queryClient = useQueryClient();
  const today = new Date();
  const year = today.getFullYear();
  const month = ("0" + (today.getMonth() + 1)).slice(-2);
  const day = ("0" + today.getDate()).slice(-2);
  const businessDate = `${year}-${month}-${day}`;
  const getLikeUpSell = () => {
    let String = '&';
    const res = cartItems?.map((item, index) => {
      return String.concat(
        `items[${index}][id]=${item.item_id}&items[${index}][size]=${item?.item_size}`,
      );
    });
    return res?.join()?.replace(/,/g, '')
  }

  const getCartId = () => {
    return cartItems?.length ? cartItems[0]?.cart_id : null;
  }

  const syncCartWihOffer = async () => {
    await syncCart(location_id, false, selectedOfferItem);
    setReFetchingCart(false);
  }
  
  const syncLocationWithBE = () => {
    const pickupOptions = locationInfo?.selectedStore?.order_types?.Pickup || [];
    const hasLegacyGoodLifeLane = pickupOptions?.some(
      (p) => p.value === PICK_UP_METHOD_LEGACY_GOOD_LIFE_LANE
    );
    if (!hasLegacyGoodLifeLane) return;
    return locationMenuService
      .getLocationDetails(location_id)
      .then((newLocationInfo) => setLocation(locationDispatch, newLocationInfo.data.data));
  };
  useEffect(() => {
    if (!authInfo.userId || !location_id) return;
    // Cancel any in-flight cart requests before starting a new sync
    orderMicroService.cancel();
    if (authInfo.userId || location_id) {
      syncLocationWithBE();
      setReFetchingCart(true);
      syncCartWihOffer();
    }
    // Also cancel on cleanup to avoid late responses overwriting newer state
    return () => {
      orderMicroService.cancel();
    };
  }, [location_id, cart?.orderType]);

  // const { data, refetch: fetchUpsell, isFetching: isFetchingUpsell } = useSuggestedItems({
  //   business_date: businessDate,
  //   hour: '12',
  //   location_id: locationInfo.selectedStore.id,
  //   items: getLikeUpSell(),
  //   customer_id: authInfo.userId,
  //   cart_id: getCartId(),
  //   destination_id: getDestination(cart?.orderType)?.destination_id
  // });

  const {
    data: upselData,
    refetch: fetchUpsell,
    isFetching: isFetchingUpsell,
  } = useGetUpsellItemsForAddToOrder({
    location_id,
    category_id: "0",
    order_type:
      cart?.orderType === DELIVERY_ORDER_TYPE
        ? DELIVERY_DESTINATION_ID
        : IN_STORE_DESTINATION_ID,
  });
  const user = getUser();
  const { data: allRewards = {}, isFetching: isFetchingRewards } = useGetRewards(
    user.id,
    user.type
  );
  const [myRewards, setMyRewards] = useState([]);
  useEffect(() => {
    if (!isFetchingRewards) {
      setMyRewards(allRewards?.rewards?.filter(reward => {
        return !reward.is_redeemed && !reward.is_expired
      }) || []);
    }
  }, [isFetchingRewards]);
  
  const [offerEligibilityModal, setOfferEligibilityModal] = useState<{
    show: boolean;
    failedOfferCriteria: string;
    redeemedItemNames: string[];
  }>({
    show: false,
    failedOfferCriteria: "",
    redeemedItemNames: [],
  });
  const [isPromoOfferAdded, setIsPromoOfferAdded] = useState<boolean>(false);
  const [onCartOpenSelectOffer, setOnCartOpenSelectOffer] = useState<boolean>(true);
  const [previousSelectedOffer, setPreviousSelectedOffer] = useState<IOfferItem>(null);
  const { redeemOffer, removeOffer } = useRedeemOffer()
  const isItMobile = useCheckMobileScreen();
  const isDesignV2 = useIsDesignV2();
  const {
    data: allOffers = {},
    isFetching,
    refetch: refetchOffers,
    isRefetching: isRefetchingOffers,
  } = useGetOffers(getUser().type === GUEST_USER, "CartModal");
  
  const offers = allOffers?.rewards || [];

  const cartRedirectOfferId = cart.redirect_offer_id;
  const cartRedirectOffer : IOfferItem = offers?.find(
    (offer: IOfferItem) => offer.id === cartRedirectOfferId
  );

  const latestOffer = offers?.[0];
  const selectedOfferItem: IOfferItem = offers?.find((offer: IOfferItem) => offer.in_cart);
  
  useEffect( () => {
    if (!isFetching && !syncingCart && (isPromoOfferAdded || onCartOpenSelectOffer)) {
      const offerToBeSelected = cartRedirectOffer || latestOffer;
      if (!offerToBeSelected) return;
      const {
        shouldShowOfferEligibilityModal,
        failedOfferCriteria,
        redeemedItemNames,
      } = redeemOffer({
        offerItem: offerToBeSelected,
        cartItems,
        selectedOfferItem,
        rewardsDiscountedSubTotal,
      });
      if ((!onCartOpenSelectOffer || cartRedirectOffer) && shouldShowOfferEligibilityModal) {
        const findPreviousOffer = offers?.find((offer: IOfferItem) => offer.id === previousSelectedOffer?.id);
        if (findPreviousOffer) {
          findPreviousOffer.in_cart = true;
          findPreviousOffer.redeemed_by_offer_discount = previousSelectedOffer.redeemed_by_offer_discount;
          findPreviousOffer.cart_item_index = previousSelectedOffer.cart_item_index;
        }
        setOfferEligibilityModal({show: true, failedOfferCriteria, redeemedItemNames});
      }
      setPreviousSelectedOffer(null);
      setIsPromoOfferAdded(false);
      setOnCartOpenSelectOffer(false);
    }
  }, [isFetching, syncingCart])


  const [stickyModal, setstickyModal] = useState<boolean>(true);
  const modalBodyRef = useRef(null);
  const config = { subtree: true, childList: true };
  const dispatch = useDispatch();
  const { mutate: toggleAutoRedeem, isLoading: isTogglingAutoRedeem } = useToggleAutoRedeem()
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isThisMobile = useCheckMobileScreen();

  const isCouponCodeEntered = couponCode?.toString()?.length > 0 ? false : true;
  const handleApplyCoupon = async (showToast:boolean) => {
    const payload: IUseCoupon = {
      promo_code: couponCode,
      location_id,
      source: isThisMobile ? 'Mobile Web' : 'Web',
    };
    applyCoupon(payload, {
      onSuccess: async (res:any) => {
        if(res?.data?.rewards?.length)
        {
          refetchOffers();
          setIsPromoOfferAdded(true);
          setPreviousSelectedOffer(selectedOfferItem);
        }
        brazeLogEventClaimPromoCode(couponCode, authInfo?.userId, cart?.orderId);
      },
      onError: (error:any) => {
        showToast && Toast_Func({ status: false, message: error?.response?.data?.errors[0] });
        bugSnagNotifyAPIError("Apply Promo Code", payload, error?.response?.data?.errors[0]);
      },
    });
  };

  const handlePromoOnEnter = (event: any) => {
    if(event?.key === "Enter")
      {
        handleApplyCoupon(true);
      }
  }

  const { loyalityPoints, tempLoyalityPoints }=useAppSelector(state=>state.loyalty)

  useEffect(() => {
    dispatch(resetCartOffer());
    dispatch(
      updateCartRedeemedOfferItem({
        is_redeemed_by_offer: false,
      }),
    );
  }, [])

  useEffect(() => {
    const pointsRedeemedInCart = getPointsRedeemedInCart(cartItems);
    dispatch(updateTempLoyalityPoints(loyalityPoints - pointsRedeemedInCart));
  }, [loyalityPoints, cartItems]);
  
  const buttonForCouponField = () => {
    if (isApplyingCoupon || isRefetchingOffers) {
      return (
        <div className={`${promoStyleClasses.loader_wrapper}`}>
          <Spinner
            animation="border"
            variant="dark"
            size="sm"
            className={promoStyleClasses.spinner}
          />
        </div>
      );
    }
    return (
      <button
        onClick={() => handleApplyCoupon(true)}
        type="button"
        className={`${promoStyleClasses.arrow_btn} btn btn-link`}
        disabled={isCouponCodeEntered}
      ></button>
    );
  };

  const onCouponInputChange = (event:any) => {
    setCouponCode(event?.target?.value)
  }

  const handleAutoUseReward = async () => {
    if (isUpdatingQuantity) return;
    setIsLoading(true)
    dispatch(setAutoRedeem(!autoRedeem));
    setMyRewards(rewards => rewards.map(reward => { return { ...reward, is_active_in_cart: !autoRedeem} }));
    let mappedRewards = {}
    cartItems.map((item: cartItem) => {
      if(item?.reward_redeemable && item?.quantity === 1)
      { mappedRewards[item.id] = item?.reward_id }
    })
    toggleAutoRedeem({
      rewards: mappedRewards,
      auto_redeem_rewards: !cart.auto_redeem_rewards,
      customer_id: authInfo.userId, location_id }, {
      onSuccess: async () => {
        setIsLoading(false)        
      },
      onError: (error: any) => {
        Toast_Func({ status: false, message: error.message })
        dispatch(setAutoRedeem(autoRedeem));
        setMyRewards(rewards => rewards.map(reward => { return { ...reward, is_active_in_cart: autoRedeem} }));
        setIsLoading(false)
      }
    })
  };
  const isSticky = (parentElement: any) => {
    const windowHeight = window.innerHeight;
    const childCount = parentElement.children.length;
    let childrenTotalHeight = 0;
    //Ignoring first and last item in loop because those are paddings for chekcout button and heading
    for (let i = 1; i < childCount - 1; i++) {
      childrenTotalHeight =
        childrenTotalHeight + parentElement.children[i].clientHeight;
    }
    return windowHeight <= childrenTotalHeight;
  };
  const observer = new MutationObserver((mutations: any) => {
    setstickyModal(isSticky(modalBodyRef.current));
  });


  useEffect(() => {
    if (autoRedeem) {
      queryClient.refetchQueries(["myRewards", authInfo.userId], null, { throwOnError: true });
    }
    function handleCartSize() {
      if (showModal && modalBodyRef.current) {
        document.documentElement.style.setProperty(
          "--get-modal-height",
          `${modalBodyRef.current.clientHeight}`
        );
        setstickyModal(isSticky(modalBodyRef.current));
      }
    }
    handleCartSize();

    //Adding observer for the size changes of the Cart contents
    showModal &&
      observer.observe(document.getElementById("cart_Modal_body"), config);

    //Adding event listener to be called wheneve the window size changes
    window.addEventListener("resize", handleCartSize);

    //Cleanup for event listeners
    return () => {
      window.removeEventListener("resize", handleCartSize);
      observer.disconnect();
    };
  }, [showModal]);

  const reApplyOffer = (iteratingIndex = -1) => {
    selectedOfferItem.in_cart = false;
    const { failedOfferCriteria } = redeemOffer({
      offerItem: selectedOfferItem,
      cartItems, 
      selectedOfferItem, 
      rewardsDiscountedSubTotal,
      deletedItemIndex: iteratingIndex,
    })
    if (failedOfferCriteria)
      removeOffer(selectedOfferItem, true);
  }
  
  const handleItemRemove = async (item_id, iteratingIndex, closeModal) => {
    const payload: IDeleteOrderItemPayload = {
      item_id: item_id
    };
    if (authInfo?.userId) payload.customer_id = authInfo.userId;
    else payload.visitor_id = visitor_id;
    deleteItem(payload, {
      onSuccess: () => {
        if (cartItems[iteratingIndex].is_redeemed_by_offer) {
          reApplyOffer(iteratingIndex);
        }
        if(cartItems[iteratingIndex]?.reward_redeemed)
        {
          customerBusinessService.updateReward(cartItems[iteratingIndex].reward_id, {is_active_in_cart: Number(!cartItems[iteratingIndex].reward_redeemed)})
        }
        if(cartItems[iteratingIndex]?.redemption_by_points)
        {
          dispatch(updateTempLoyalityPoints(tempLoyalityPoints + parseInt(cartItems[iteratingIndex]?.redeem_points)))
        }
        dispatch(deleteItemFromOrder(iteratingIndex));
        closeModal();
        queryClient.refetchQueries(["myRewards", authInfo.userId], undefined, { throwOnError: true });
        if(cartItems.length - 1 > 0){
          fetchUpsell()
        }
      },
      onError: (error) => {
        console.log(error, "ERROR ON DELETE");
        closeModal()
      },
    });
  };

  const updateItemRedeem = (iteratingIndex: number) => {
    const item = cartItems[iteratingIndex]
    if(item.reward_redeemable && item.reward_id) {
      dispatch(updateItemKey({ index: iteratingIndex, value: true, key: "reward_redeemed" }))
    }
  }
  const handleQuantityChange = async (
    item_id,
    iteratingIndex,
    itemQuantity: number
  ) => {
    trackCartPageClick({
      action: PULSE_CLICK_ACTION.TAP_CHANGE_QUANTITY,
      label: `${item_id}, ${itemQuantity}`,
      value: itemQuantity,
      page_path: location.pathname,
    });
    const payload: IUpdateOrderQuantityPayload = {
      item_id,
      quantity: itemQuantity,
    };
    if (autoRedeem && itemQuantity === 1) updateItemRedeem(iteratingIndex)
    dispatch(
      updateItemQuantity({
        index: iteratingIndex,
        quantity: itemQuantity,
      })
    );
    if (authInfo?.userId) payload.customer_id = authInfo.userId;
    else payload.visitor_id = visitor_id;
    updateQuantity(payload, {
      onSuccess: () => {},
      onError: (error) => {
        console.log(error, "ERROR ON quantity change");
      },
    });
  };

  const shouldShowRewardsOfferPromo = () => {
    return authInfo.userId && 
    authInfo.type !== GUEST_USER && 
    shouldRelease('Features', 'redeem')
  };
  
  return (
    <>
      {(cartRedirectOffer || latestOffer) && <OfferEligibilityModal
        show={offerEligibilityModal.show}
        offerFailedCriteria={offerEligibilityModal.failedOfferCriteria}
        redeemedItemNames={offerEligibilityModal.redeemedItemNames}
        offerDetails={cartRedirectOfferId ? cartRedirectOffer?.data : latestOffer?.data}
        closeModal={() => 
          setOfferEligibilityModal({show: false, failedOfferCriteria: "", redeemedItemNames: []})
        }
      />}
      <Modal
        show={showModal}
        onHide={closeModal}
        id="cart_modal"
        backdrop={false}
        className={`${styleClasses.my_card_modal
          } my-card-modal app_container_wrap right ${isDesignV2 ? `cart-modal-mobile-designv2 ${promoStyleClasses.cart_modal_mobile_designv2}` : '' } ${stickyModal ? styleClasses.sticky_modal_content : ""
          }`}
      >
        <Modal.Header className={`${styleClasses.modal_header} ${isDesignV2 ? 'cart-modal-header-mobile-designv2' : 'pt-3 pb-0'}`}>
          <div
            className={`${styleClasses.customize_card_header} ${isDesignV2 ? 'customize-card-header-mobile-desigv2' :  ''} w-100 ps-0 mb-0 text-md-end text-start`}
          >
            <button
              className={`${styleClasses.modal_close_button} close modal-close-button p-0 mb-lg-3 d-none d-md-block ms-auto`}
              onClick={closeModal}
            >
              Close
            </button>
            <Link
              to={ROUTE_MENU}
              onClick={() => {
                trackCartPageClick({
                  action: PULSE_CLICK_ACTION.TAP_BACK_TO_MENU,
                  label: null,
                  page_path: location.pathname,
                });
                closeModal();
              }}
              className={`btn btn-custom back-arrow-btn f-s14 px-0 d-md-none ${isDesignV2 ? 'f-w5 mb-0  pb-0 pt-0' : 'mb-3'}`}
            >
              {
                isDesignV2 ? 'Back' : 'Menu'
              }
            </Link>
          {!isDesignV2 &&  <h3
              className={`${styleClasses.card_title} text-md-center d-inine-block font-Cls mb-0 f-s22 lh-normal text-start`}
            >
              Cart Details
            </h3>}
          </div>
        </Modal.Header>
        <Modal.Body
          id="cart_Modal_body"
          className={`${styleClasses.cart_Modal_body} ${isCartEmpty && styleClasses.emptyCart_Modal_body}`}
          ref={modalBodyRef}
        >
          {isDesignV2 && !isCartEmpty && <h3
              className={`${styleClasses.card_title} text-md-center d-inine-block font-Cls mb-1 f-s20 f-w3 lh-normal text-start`}
            >
              Cart Details
            </h3>}
          {isCartEmpty && <hr className="shadow-divider mb-4 border-0"/>}
          <CartItemsList
            syncingCart={syncingCart}
            isAutoRedeeming={isLoading}
            selectedOfferItem={selectedOfferItem}
            rewardsDiscountedSubTotal={rewardsDiscountedSubTotal}
            handleItemRemove={handleItemRemove}
            loading={isDeleting || isUpdatingQuantity}
            handleQuantityChange={handleQuantityChange}
            allRewards={myRewards}
            cart={cartItems}
            scrollcart={styleClasses.scroll_body_cart}
            setMyRewards={setMyRewards}
          />
          {(!isCartEmpty && !syncingCart) || authInfo.type !== GUEST_USER ? <hr className="shadow-divider mb-4 border-0" />: null}
          {!isCartEmpty ?
            <>
              <UpsellItemsLists data={upselData} cart={cartItems} isFetchingUpsell={isFetchingUpsell} fetchUpsell={fetchUpsell} syncCart={syncCartWihOffer} />
            </> : null
          }
          {shouldShowRewardsOfferPromo() && myRewards?.length > 0 ? (
            <>
              <AutoRedeemToggle isLoading={isLoading} autoRedeem={autoRedeem} handleAutoUseReward={handleAutoUseReward} />
            </>
          ): null}
          {shouldShowRewardsOfferPromo() && myRewards.length ? (
            <>
              <SuggestedItemsList
                addToOrder={addToOrder}
                list={myRewards}
                closeModal={closeModal}
                title="Redeem a Reward"
                isRewardList={true}
                setMyRewards={setMyRewards}
              />
              <hr className="shadow-divider mb-4 border-0" />
            </>
          ): null }
              {shouldShowRewardsOfferPromo() && (
          <>
            <div
              className={`${promoStyleClasses.promo_code} ${isDesignV2 ? promoStyleClasses.promo_code_mobile_designv2 : ''} new_form_design promo_code`}
            >
              <div className={`form-group mb-0 ${isDesignV2 ? 'position-relative' : ''}`}>
                {isDesignV2 && !Boolean(couponCode) && <label className="mb-1">Enter Promo code</label>}
                {!isDesignV2 && <label className="mb-1">Enter Promo Codes Here</label>}
                <div className={promoStyleClasses.promoInputBtn}>
                <input
                  type="text"
                  className={`form-control f-s24`}
                  value={couponCode}
                  onChange={(event) => onCouponInputChange(event)}
                  onKeyDown={ (event) => handlePromoOnEnter(event) }
                />
                {buttonForCouponField()}
                </div>
              </div>
            </div>
           {!isDesignV2 && <hr className="shadow-divider mb-4 border-0" />}
          </>
          )}
          {shouldShowRewardsOfferPromo() ? (
            <OfferItemsList
              addToOrder={() => {}}
              offers={offers}
              selectedOfferItem={selectedOfferItem}
              closeModal={closeModal}
              title="Offers"
              isRewardList={true}
              rewardsDiscountedSubTotal={rewardsDiscountedSubTotal}
              isDisabled = {syncingCart}
            />) : null
          }
          { !isCartEmpty || authInfo.type !== GUEST_USER ? 
            <Billing
              cartItems={cart?.items}
              isOrderTypeSelected={cart?.isOrderTypeSelected}
              orderType={cart?.orderType}
              authInfo={authInfo}
              CartBtnCheckout={styleClasses.cart_btn_checkout}
              TotalList={styleClasses.total_list}
              offerDiscount={cart?.offerDiscount}
              disableCheckout={syncingCart}
              handleTax={false}
              setRewardsDiscountedSubTotal={setRewardsDiscountedSubTotal}
              offerDiscountType={cart?.offerDiscountType}
              />
            : null
          }
        </Modal.Body>
      </Modal>
    </>
  );
}